import React, { SyntheticEvent, useState } from 'react';
import {
    alpha,
    Box,
    ClickAwayListener,
    Collapse,
    styled,
    Tooltip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { makeTransitions, TRANSITION_TIME } from 'styles/theme';
import Flex from 'ui/Flex/Flex';
import { SideButton } from './SideButton';
import { SideBarMode } from '../hooks';

type Props = {
    children?: React.ReactNode;
    icon: JSX.Element;
    text: string;
    sideBarMode: SideBarMode;
    isSideBarExpanded: boolean;
    setExpanded: DispatchState<boolean>;
    isAvailable?: boolean;
    isSelected?: boolean;
    to?: string;
    href?: string;
    onClick?: (e: SyntheticEvent) => void;
    className?: string;
    showTooltip?: boolean;
};

const StySideButton = styled(SideButton, {
    name: 'StySideButton',
    shouldForwardProp(propName) {
        return propName !== 'expanded' && propName !== 'selected';
    },
})<{ expanded: boolean; selected: boolean }>(
    ({ expanded, selected, theme }) => ({
        justifyContent: 'flex-start',
        transition: makeTransitions([
            'background-color',
            'color',
            'max-width',
            'min-width',
        ]),
        overflow: 'hidden',
        minHeight: '2.5rem',
        maxHeight: '2.5rem',
        minWidth: expanded ? '11.25rem' : '2.5rem',
        maxWidth: expanded ? '11.25rem' : '2.5rem',
        '.MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: '8px',
        },
        color: 'white',
        '&:hover': {
            color: theme.palette.primary.main,
            background: 'white',
        },
        background: selected ? alpha('#FFFFFF', 0.25) : 'transparent',
        margin: '0.75rem 0',
    }),
);

const MenuSideButton = ({
    children = null,
    icon,
    text,
    sideBarMode,
    isSideBarExpanded,
    setExpanded,
    isAvailable = true,
    isSelected = false,
    to: toProp = '',
    href = '',
    onClick: onClickProp = () => {},
    className,
    showTooltip = true,
}: Props) => {
    // This state is used to tell if the user is trying to navigate into the sub-menu
    // items of this menu item.
    // It is useful when the the current location is in another menu item and we want
    // to show the sub-menu items of this menu item.
    // Example: the user is in Budget, clicks on Décision, they need to see the sub-items
    //          of Décision.
    const [isNavigatingInChildren, setIsNavigatingInChildren] = useState(false);

    // if the menu item has sub-menu buttons and it's available, it toggles the SideBar.
    // if it does not, it behaves as a simple navigation.
    const onClick = (e: SyntheticEvent) => {
        if (onClickProp) onClickProp(e);
        if (sideBarMode === 'preferCollapsed') {
            if (children == null || !isAvailable) return;
            if (!isSideBarExpanded) {
                setIsNavigatingInChildren(true);
                setExpanded(true);
                /* we are clicking on a button that will expand the sidebar,
                 ** we don't want to navigate to the link. */
                e.preventDefault();
                return;
            }
            if (!isNavigatingInChildren) {
                setIsNavigatingInChildren(true);
                return;
            }
            setExpanded(false);
            return;
        }
        setIsNavigatingInChildren(true);
    };

    const shouldExpandSideBarOnClick =
        !isSideBarExpanded && children && isAvailable;
    // If the menu item button doesn't have to expand the sidebar:
    // - it performs a simple navigation.
    // If the button shouldn't perform a navigation:
    // - we pass '#' as the 'to' prop. This is important to disable navigation
    //   and at the same time keep a Link component that will animate correctly.
    const to = shouldExpandSideBarOnClick ? '#' : toProp;

    // The Box is necessary for the ClickAwayListener and the Tooltip to work.
    const button = (
        <Box>
            <StySideButton
                selected={isSelected}
                expanded={isSideBarExpanded}
                text={text}
                icon={icon}
                onClick={onClick}
                to={to}
                href={href}
                className={className}
            >
                {!isAvailable && isSideBarExpanded ? (
                    <LockIcon fontSize="small" />
                ) : (
                    <></>
                )}
            </StySideButton>
        </Box>
    );

    return (
        <Box>
            <ClickAwayListener
                onClickAway={() => {
                    setIsNavigatingInChildren(false);
                }}
                // disable listening when not necessary.
                mouseEvent={children != null ? 'onClick' : false}
                touchEvent={children != null ? 'onTouchEnd' : false}
            >
                <Tooltip
                    title={!isSideBarExpanded && showTooltip ? text : ''}
                    placement="right"
                    PopperProps={{
                        sx: {
                            // Overriding the style of the popped tooltip is quite hard.
                            // This selector is complex so it can have a higher specificity
                            // than the rule used by Mui.
                            '&[data-popper-placement*="right"] div.MuiTooltip-tooltip':
                                {
                                    marginLeft: '1.5rem',
                                },
                        },
                    }}
                >
                    {button}
                </Tooltip>
            </ClickAwayListener>
            <Collapse
                orientation="horizontal"
                in={isSideBarExpanded}
                timeout={TRANSITION_TIME}
            >
                <Collapse
                    in={
                        isAvailable &&
                        isSideBarExpanded &&
                        (isSelected || isNavigatingInChildren)
                    }
                    timeout={TRANSITION_TIME}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                >
                    <Flex direction="column">{children}</Flex>
                </Collapse>
            </Collapse>
        </Box>
    );
};

export default MenuSideButton;
